/** Apply payments to payables
 *
 *  The payments argument objects will have to have at least these props:
 *
 *  { txnDate, unappliedAmount = 0 }
 *
 *  The payables argument objects will have to have at least these props:
 *
 *  { dueDate, amount, balance = 0, serviceFee = 0 }
 *
 *  The return value is an array of payable-payments that looks like this:
 *
 *  [
 *    {
 *      appliedAmount: number,
 *      appliedServiceFee: number,
 *      payment: <payment-object>,
 *      payable: <payable-object>
 *    },
 *  ]
 *
 *  The <payment-object> and <payable-object> will be the source objects from the
 *  payaments and payables argument arrays. They will not have been mutated.
 *  In order to update the balance of any of these, the `appliedAmount` needs
 *  to be added.
 */
function applyPayments (payments, payables) {
  const pas = payables.map(p => ({
    date: p.dueDate,
    amount: p.amount,
    balance: p.balance,
    serviceFee: p.serviceFee || 0,
    serviceFeeBalance: 0,
    source: p
  })).sort((a, b) => a.date < b.date ? -1 : 1)

  const pms = payments.map(p => ({
    date: p.txnDate,
    unappliedAmount: p.unappliedAmount || 0,
    source: p
  })).sort((a, b) => a.date < b.date ? -1 : 1)

  const payablePayments = []
  let paIndex = 0
  let pmIndex = 0
  while (paIndex < pas.length && pmIndex < pms.length) {
    const payable = pas[paIndex]
    const payment = pms[pmIndex]
    let unpaidAmount = payable.balance
    let unappliedAmount = payment.unappliedAmount || 0

    if (unpaidAmount === 0) {
      paIndex++
      continue
    }
    if (unappliedAmount === 0) {
      pmIndex++
      continue
    }

    let appliedServiceFee = 0
    if (payable.serviceFeeBalance < payable.serviceFee) {
      const unpaidServiceFee = payable.serviceFee - payable.serviceFeeBalance
      appliedServiceFee = Math.min(unpaidServiceFee, unappliedAmount)
      payable.balance -= appliedServiceFee
      payment.unappliedAmount -= appliedServiceFee
      unappliedAmount -= appliedServiceFee
      unpaidAmount -= appliedServiceFee
    }

    const appliedAmount = Math.min(unpaidAmount, unappliedAmount)
    payable.balance -= appliedAmount
    payment.unappliedAmount -= appliedAmount

    payablePayments.push({
      appliedAmount,
      appliedServiceFee,
      payable: payable.source,
      payment: payment.source
    })
  }
  return payablePayments
}

module.exports = {
  applyPayments
}
